import { useMemo } from 'react';
import {
  AttributeType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { useHasRoiFeature } from '../../hooks/use-has-roi-feature';
import { useRoiRequestPayload, type UseRoiRequestBaseProps } from '../../hooks/use-roi-request-payload';
import { detailedConversionCategorized } from '../demo-data/detailed-conversion';

type UseFetchMissedCallTextAttributionProps = {
  categorizedConversions?: Record<string, number>;
  isLoading?: boolean;
  sortedConversionTypes?: string[];
};

// Translation is not required for these as it is being used to sort the keys and read the values from BE response
const allConversionTypes: string[] = ['Same Day', '7 Days', '14 Days', '21 Days', '28 Days'];

export const useFetchMissedCallTextAttribution = (
  props: UseRoiRequestBaseProps
): UseFetchMissedCallTextAttributionProps => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { isFeatureEnabled: isMCTRoiEligibleLocation } = useHasRoiFeature({ roi: 'missedCallText' });
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...props,
    additionalRequestPayload: {
      attributeType: AttributeType.FORWARD,
      roiType: RoiType.MISSED_CALL_TEXT,
    },
    isDemoAccount,
  });

  const { data: conversionData, isLoading } = AnalyticsGatewayV1.Queries.useGetRoiChartViewQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest && isMCTRoiEligibleLocation,
      onError: () => {
        alert.error(t("Couldn't fetch the missed call text attribution. Please try again."));
      },
    },
  });

  const categoryConversions = useMemo(() => {
    const categorizedConversionsMap: Record<string, number> = {};
    const keysInData: string[] = [];
    const data = isDemoAccount ? detailedConversionCategorized : conversionData;

    data?.data?.forEach(({ conversionType = '', count = 0 }) => {
      categorizedConversionsMap[conversionType] = count;
      if (!keysInData.includes(conversionType)) {
        keysInData.push(conversionType);
      }
    });

    return {
      categorizedConversions: categorizedConversionsMap,
      sortedConversionTypes: allConversionTypes.filter((key) => keysInData.includes(key)),
    };
  }, [conversionData, isDemoAccount]);

  return {
    isLoading,
    ...categoryConversions,
  };
};
